import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Phone, PhoneCall, Voicemail, CheckCircle, AlertTriangle, RefreshCw, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';

export default function VoipPage({ API_BASE }) {
  const [status, setStatus] = useState(null);
  const [vm, setVm]         = useState([]);
  const [showDial, setShowDial] = useState(false);
  const [loading, setL]     = useState(true);

  const load = useCallback(async () => {
    setL(true);
    try {
      const [s, v] = await Promise.all([
        axios.get(`${API_BASE}/voip/status`),
        axios.get(`${API_BASE}/voip/voicemails`),
      ]);
      setStatus(s.data); setVm(v.data || []);
    } catch (e) { console.error(e); }
    setL(false);
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign:'center', color: theme.textMuted }}>Loading...</div>;

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:18 }}>
        <h2 style={{ margin:0, color: theme.navy }}>☎️ VoIP — Built-in Clinic Phone</h2>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={load} style={btn(theme.secondary)}><RefreshCw size={14}/> Reload</button>
          <button onClick={()=>setShowDial(true)} style={btn(theme.primary)}><PhoneCall size={14}/> Click-to-Call</button>
        </div>
      </div>

      <div style={{ ...cardStyle, padding:14, marginBottom:14, borderLeft:`4px solid ${status?.configured ? theme.success : theme.warning}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {status?.configured ? <CheckCircle color={theme.success}/> : <AlertTriangle color={theme.warning}/>}
          <div>
            <strong style={{ color: theme.navy }}>Provider:</strong> <code>{status?.provider}</code> · {status?.configured ? <span style={{ color: theme.success }}>configured ✓</span> : <span style={{ color: theme.warning }}>stub mode</span>}
            {status?.notes && <div style={{ fontSize:12, color: theme.textMuted, marginTop:4 }}>{status.notes}</div>}
          </div>
        </div>
        {!status?.configured && (
          <div style={{ marginTop:10, padding:10, background: '#fef3c7', borderRadius: theme.radiusSm, fontSize:12, color: '#92400e' }}>
            <strong>Setup:</strong> server <code>.env</code> me <code>VOIP_PROVIDER=exotel</code> + <code>EXOTEL_SID</code>, <code>EXOTEL_API_KEY</code>, <code>EXOTEL_API_TOKEN</code>, <code>EXOTEL_VIRTUAL_NUMBER</code> set karo. Webhook URL Exotel dashboard me <code>{API_BASE}/voip/webhook</code> de do.
          </div>
        )}
      </div>

      <div style={{ ...cardStyle, padding:14, marginBottom:18, background: theme.secondaryLight }}>
        <div style={{ fontWeight:700, color: theme.secondaryDark }}>How it works</div>
        <ul style={{ margin:'8px 0 0 18px', fontSize: 13, color: theme.navy, lineHeight: 1.7 }}>
          <li><strong>Click-to-Call:</strong> staff clicks → server pings Exotel → Exotel rings staff first → bridges to patient (clinic ka virtual number patient ko dikhega, not staff's personal)</li>
          <li><strong>Inbound:</strong> patient calls clinic number → Exotel webhook hits server → CallLog auto-create → AI Call Intelligence transcript pe automatically chal sakta hai</li>
          <li><strong>Voicemail:</strong> after-hours calls voicemail me jaate hain → audio download + transcribe</li>
          <li><strong>Recording:</strong> har call auto-recorded (Exotel storage) → recording URL CallLog me save</li>
        </ul>
      </div>

      <h3 style={{ margin:'18px 0 10px', color: theme.navy }}>📩 Voicemails ({vm.length})</h3>
      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        {vm.length === 0 ? (
          <div style={{ padding:30, textAlign:'center', color: theme.textMuted }}>
            <Voicemail size={28} style={{ opacity:.4 }}/><div style={{ marginTop:6 }}>No voicemails.</div>
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background: theme.primaryLight }}>
              <tr><th style={th}>When</th><th style={th}>From</th><th style={th}>Patient</th><th style={th}>Duration</th><th style={th}>Recording</th></tr>
            </thead>
            <tbody>
              {vm.map(v => (
                <tr key={v._id} style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                  <td style={td}>{new Date(v.createdAt).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</td>
                  <td style={td}><code>{v.fromNumber}</code></td>
                  <td style={td}>{v.patientId?.name || <span style={{ color: theme.textMuted }}>—</span>}</td>
                  <td style={td}>{v.durationSec ? Math.round(v.durationSec/60) + 'm' : '—'}</td>
                  <td style={td}>{v.recordingUrl ? <audio controls src={v.recordingUrl} style={{ height: 30 }}/> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showDial && <DialModal API_BASE={API_BASE} onClose={()=>setShowDial(false)} onSent={()=>{ setShowDial(false); load(); }}/>}
    </div>
  );
}

function DialModal({ API_BASE, onClose, onSent }) {
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState('');
  const [label, setLab] = useState('');
  const [busy, setB]    = useState(false);

  const dial = async () => {
    if (!from || !to) return alert('Both numbers required');
    setB(true);
    try {
      const r = await axios.post(`${API_BASE}/voip/click-to-call`, { fromNumber: from, toNumber: to, label });
      alert(r.data.provider === 'stub' ? '⚠️ Stub mode — call not actually placed. Configure provider to enable real calls.' : `✅ Call initiated — ${r.data.providerCallId}`);
      onSent();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setB(false);
  };

  return (
    <div style={overlay}>
      <div style={{ ...cardStyle, padding:24, width:'min(440px, 92vw)' }}>
        <div style={{ display:'flex', justifyContent:'space-between' }}>
          <h3 style={{ margin:0 }}>📞 Click-to-Call</h3>
          <button onClick={onClose} style={{ background:'transparent', border:'none', cursor:'pointer' }}><X/></button>
        </div>
        <div style={{ marginTop:14 }}>
          <label style={labelStyle}>Staff number (will ring first)</label>
          <input value={from} onChange={e=>setFrom(e.target.value)} style={inputStyle} placeholder="+91 98XXXXXXXX"/>
          <label style={labelStyle}>Patient number (will ring second)</label>
          <input value={to} onChange={e=>setTo(e.target.value)} style={inputStyle} placeholder="+91 98XXXXXXXX"/>
          <label style={labelStyle}>Label (optional)</label>
          <input value={label} onChange={e=>setLab(e.target.value)} style={inputStyle} placeholder="Recall reminder · Sharma family"/>
        </div>
        <button onClick={dial} disabled={busy} style={{ ...btn(theme.primary), marginTop:14, width:'100%' }}>
          <Phone size={14}/> {busy ? 'Bridging...' : 'Place Call'}
        </button>
      </div>
    </div>
  );
}

const th = { padding:'10px 12px', textAlign:'left', fontSize:12, color: theme.navy, fontWeight:700 };
const td = { padding:'10px 12px', fontSize:13 };
const btn = (c) => ({ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px', background:c, color:'#fff', border:'none', borderRadius: theme.radiusSm, cursor:'pointer', fontWeight:600, fontSize:13 });
const overlay = { position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 1000, padding: 16 };
